import { copy } from "angular";
import { IWhiteboardShapeState } from "../shapes";
import { IWhiteboardLinkState, linkId } from "../links";
import { WhiteboardStateHolder } from "./whiteboard-state-holder";
import { IWhiteboardStateChangeNotifier, nullWhiteboardStateChangeNotifier, IWhiteboardStateChange } from "./state-change-notifier";

interface ITransaction {
  message: string;
  apply(): void;
  revert(): void;
}

interface ITransactionActions {
  apply(changes: IWhiteboardStateChange[]): void;
  revert(changes: IWhiteboardStateChange[]): void;
}

export const shapeChange = (operation: "add" | "replace" | "remove", shape: IWhiteboardShapeState): IWhiteboardStateChange => {
  const shapeId = shape.type === "alert" && shape.original ? shape.original.id : shape.id;
  return { op: operation, path: `shapes/${shapeId}`, value: shape };
};

const linkChange = (operation: "add" | "remove", link: IWhiteboardLinkState): IWhiteboardStateChange => {
  return { op: operation, path: `links/${linkId(link)}`, value: link };
};

export interface ITransactionChangeHistoryTracker {
  applyShapeChanges(shapeId: string, changes: Record<string, unknown>): this;
  updateShape(...shapes: IWhiteboardShapeState[]): this;
  deleteShape(...shapes: IWhiteboardShapeState[]): this;
  updateLink(...links: IWhiteboardLinkState[]): this;
  deleteLink(...links: IWhiteboardLinkState[]): this;

  /**
   * Applies the uncommitted changes and commits the transaction
   * so that it can be reverted later by `undo`. Additionally, it calls
   * an external notifier with the committed changes. By specifying
   * `{ undo: false }` the undo can be prevented; by specifying
   * `{ notify: false }` the notification can be prevented
   */
  commit(options?: { undo?: boolean; notify?: boolean }): void;

  /**
   * Removes any uncommitted changes without applying them
   */
  abort(): void;
}

export class ChangeHistoryTracker implements ITransactionChangeHistoryTracker {
  private undoTransactions: ITransaction[] = [];
  private redoTransactions: ITransaction[] = [];
  private uncommitted: { actions: ITransactionActions[]; message: string };

  constructor(private stateHolder: WhiteboardStateHolder, private stateChangeNotifier: IWhiteboardStateChangeNotifier = nullWhiteboardStateChangeNotifier) {}

  applyShapeChanges(shapeId: string, changes: Record<string, unknown>): this {
    this.ensureInTransaction();

    const shape = this.stateHolder.findShape(shapeId);
    if (!shape) {
      throw new Error(`No such shape with ID ${shapeId}`);
    }

    const reverseChanges = Object.keys(changes).reduce((result, key) => Object.assign(result, { [key]: copy(shape[key]) }), {});
    this.uncommitted.actions.push({
      apply: (stateChanges) => {
        this.stateHolder.applyShapeChanges(shapeId, changes);
        stateChanges.push(shapeChange("replace", { ...shape, ...changes }));
      },
      revert: (stateChanges) => {
        this.stateHolder.applyShapeChanges(shapeId, reverseChanges);
        stateChanges.push(shapeChange("replace", { ...shape, ...reverseChanges }));
      },
    });
    return this;
  }

  updateShape(...shapes: IWhiteboardShapeState[]): this {
    this.ensureInTransaction();

    for (const shape of shapes) {
      const prevShape = this.stateHolder.findShape(shape.id);

      this.uncommitted.actions.push({
        apply: (stateChanges) => {
          this.stateHolder.updateShape(shape);
          if (prevShape) {
            stateChanges.push(shapeChange("replace", shape));
          } else {
            stateChanges.push(shapeChange("add", shape));
          }
        },
        revert: (stateChanges) => {
          if (prevShape) {
            this.stateHolder.updateShape(prevShape);
            stateChanges.push(shapeChange("replace", prevShape));
          } else {
            this.stateHolder.deleteShape(shape.id);
            stateChanges.push(shapeChange("remove", shape));
          }
        },
      });
    }
    return this;
  }

  deleteShape(...shapes: IWhiteboardShapeState[]): this {
    this.ensureInTransaction();

    for (const shape of shapes) {
      this.uncommitted.actions.push({
        apply: (stateChanges) => {
          this.stateHolder.deleteShape(shape.id);
          stateChanges.push(shapeChange("remove", shape));
        },
        revert: (stateChanges) => {
          this.stateHolder.updateShape(shape);
          stateChanges.push(shapeChange("add", shape));
        },
      });
    }
    return this;
  }

  updateLink(...links: IWhiteboardLinkState[]): this {
    this.ensureInTransaction();

    this.uncommitted.actions.push({
      apply: (stateChanges) => {
        this.stateHolder.updateLink(...links);
        for (const link of links) {
          stateChanges.push(linkChange("add", link));
        }
      },
      revert: (stateChanges) => {
        this.stateHolder.deleteLink(...links);
        for (const link of links) {
          stateChanges.push(linkChange("remove", link));
        }
      },
    });
    return this;
  }

  deleteLink(...links: IWhiteboardLinkState[]): this {
    this.ensureInTransaction();

    this.uncommitted.actions.push({
      // eslint-disable-next-line sonarjs/no-identical-functions
      apply: (stateChanges) => {
        this.stateHolder.deleteLink(...links);
        for (const link of links) {
          stateChanges.push(linkChange("remove", link));
        }
      },
      // eslint-disable-next-line sonarjs/no-identical-functions
      revert: (stateChanges) => {
        this.stateHolder.updateLink(...links);
        for (const link of links) {
          stateChanges.push(linkChange("add", link));
        }
      },
    });
    return this;
  }

  beginTransaction(message: string): this {
    this.uncommitted = { actions: [], message };
    return this;
  }

  commit(options: { undo?: boolean; notify?: boolean } = {}): void {
    this.ensureInTransaction();

    const uncommittedActions = this.uncommitted.actions;
    if (uncommittedActions.length) {
      const transaction: ITransaction = {
        message: this.uncommitted.message,
        apply: () => {
          const stateChanges: IWhiteboardStateChange[] = [];
          [...uncommittedActions].forEach((action) => action.apply(stateChanges));
          if (options.notify !== false && stateChanges.length) {
            this.stateChangeNotifier.stateChange(stateChanges);
            this.stateHolder.markStateAsDirty();
          }
        },
        revert: () => {
          const stateChanges: IWhiteboardStateChange[] = [];
          [...uncommittedActions].reverse().forEach((action) => action.revert(stateChanges));
          if (options.notify !== false && stateChanges.length) {
            this.stateChangeNotifier.stateChange(stateChanges);
            this.stateHolder.markStateAsDirty();
          }
        },
      };

      if (options.undo !== false) {
        this.redoTransactions = [];
        this.undoTransactions.push(transaction);
      }

      transaction.apply();
    }

    this.abort();
  }

  abort(): void {
    this.ensureInTransaction();

    delete this.uncommitted;
  }

  undo(): boolean {
    this.ensureNotInTransaction();

    const last = this.undoTransactions.pop();
    if (!last) {
      return false;
    }

    last.revert();
    this.redoTransactions.push(last);
    return true;
  }

  redo(): boolean {
    this.ensureNotInTransaction();

    const last = this.redoTransactions.pop();
    if (!last) {
      return false;
    }

    last.apply();
    this.undoTransactions.push(last);
    return true;
  }

  private ensureInTransaction() {
    if (!this.uncommitted) {
      throw new Error("You must initiate a new transaction first");
    }
  }

  private ensureNotInTransaction() {
    if (this.uncommitted) {
      throw new Error("You cannot undo/redo while in transaction. First, commit your current one.");
    }
  }
}
